import { Icon } from '@material-ui/core'
import { USER_TYPE } from 'app/ducks/auth/types'
import { modalFormOperations } from 'app/ducks/modal'
import { Store } from 'app/redux/Store'
import React from 'react'
import School from './school/School'
import { Theme } from 'app/components/MatxTheme/themes'
import {
  getFeatureFlag,
  timestamp as featureFlagsTimestamp,
} from 'app/ducks/featureFlags/selectors'
import {
  INTERNAL_CAMPAIGNS_FLAG,
  INTERNAL_CAMPAIGNS_PATH,
  INTERNAL_CAMPAIGN_ROLES,
} from 'app/ducks/internalCampaigns/constants'
import { MCP_AUTHORIZE_PATH, MCP_SETUP_PATH, MCP_SETUP_ROLES } from './mcp/mcp'
import { SURVEY_MANAGEMENT_FLAG } from './survey/surveyManagementFlag'
import { SURVEY_BUILDER_PATH } from './survey/builder/paths'

const ComingSoon = React.lazy(() => import('./users/comingSoon'))

export const signUp = {
  path: '/session/signup',
  component: React.lazy(() =>
    import('app/views/sessions/register/JwtRegister')
  ),
  isPublic: true,
}

export const signIn = {
  path: '/session/signin',
  component: React.lazy(() => import('app/views/sessions/login/JwtLogin')),
  isPublic: true,
}

export const biometryEnroll = {
  name: 'Cadastro de biometria',
  icon: 'fingerprint',
  path: '/biometry-enroll',
  allowedOnThemes: [Theme.AMSM],
  component: React.lazy(() =>
    import('app/views/biometry-enroll/BiometryEnroll')
  ),
  roles: [USER_TYPE.BIOMETRY_MANAGER],
}

export const physicalSpaceReservations = {
  name: 'Reservas de espaço',
  icon: 'deck',
  path: '/physical-space-reservations',
  allowedOnThemes: [Theme.AMSM, Theme.CIC],
  component: React.lazy(() =>
    import('app/views/physical-space-reservations/PhysicalSpaceReservations')
  ),
  roles: [
    USER_TYPE.BIOMETRY_MANAGER,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const physicalAccess = {
  name: 'Acesso Físico',
  icon: 'arrow_circle_up',
  path: '/physical-access',
  allowedOnThemes: [Theme.AMSM],
  component: React.lazy(() =>
    import('app/views/physical-access/PhysicalAccess')
  ),
  roles: [
    USER_TYPE.COLLABORATOR,
    USER_TYPE.BIOMETRY_MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const accountActivation = {
  path: '/session/account-activation',
  component: React.lazy(() =>
    import('app/views/sessions/activation/AccountActivation')
  ),
  isPublic: true,
}

export const forgotPassword = {
  path: '/session/forgot-password',
  component: React.lazy(() => import('app/views/sessions/ForgotPassword')),
  isPublic: true,
}

export const notFound = {
  path: '/session/404',
  component: React.lazy(() => import('app/views/sessions/NotFound')),
  isPublic: true,
}

export const servicesUnavailable = {
  path: '/services-unavailable',
  component: React.lazy(() => import('app/views/sessions/ServiceUnavailable')),
  isPublic: true,
}

export const dashboard = {
  name: 'Dashboard',
  icon: 'dashboard',
  path: '/dashboard',
  component: React.lazy(() => import('app/views/dashboard/Dashboard')),
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const userDetails = {
  path: '/user/details/:id',
  exact: true,
  component: React.lazy(() =>
    import('app/views/users/UserDetails/UserDetails')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.MANAGER, USER_TYPE.COLLABORATOR],
  capability: 'view_user',
}

export const userList = {
  name: 'Listagem de usuários',
  icon: 'people',
  iconText: 'SI',
  path: '/user/list',
  exact: true,
  component: React.lazy(() => import('app/views/users/UserList')),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.MANAGER, USER_TYPE.COLLABORATOR],
  capability: 'view_user',
}

export const userForm = {
  path: '/user/form',
  exact: true,
  component: React.lazy(() => import('app/views/users/UserForm')),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.MANAGER, USER_TYPE.COLLABORATOR],
  capability: 'change_user_records',
}

export const userMergeSuggestions = {
  name: 'Merge de usuários',
  icon: 'merge_type',
  path: '/user/merge-suggestions',
  exact: true,
  component: React.lazy(() => import('app/views/users/MergeSuggestions')),
  roles: [USER_TYPE.ADMINISTRATOR],
}

export const finances = {
  name: 'Finanças',
  icon: 'attach_money',
  path: '/finances',
  exact: true,
  component: React.lazy(() => import('app/views/finances/FinancesDashboard')),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
  allowedOnThemes: [
    Theme.HALL,
    Theme.AMSM,
    Theme.ACTION,
    Theme.AFA,
    Theme.EXOS,
    Theme.CIC,
    Theme.CLAB,
    Theme.BIOACADEMIA,
  ],
}

export const billingPage = {
  name: 'Cobrança Manual',
  icon: 'attach_money',
  path: '/billing',
  exact: true,
  component: React.lazy(() =>
    import('app/views/finances/BillingPage/BillingPage')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
  allowedOnThemes: [
    Theme.HALL,
    Theme.AMSM,
    Theme.ACTION,
    Theme.AFA,
    Theme.EXOS,
    Theme.CIC,
    Theme.CLAB,
    Theme.BIOACADEMIA,
  ],
}

export const accountsPayablePage = {
  name: 'Contas a pagar',
  icon: 'attach_money',
  path: '/accounts-payable',
  exact: true,
  component: React.lazy(() =>
    import('app/views/finances/AccountsPayablePage/AccountsPayablePage')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
  allowedOnThemes: [Theme.CLAB],
}

export const accountsReceivablePage = {
  name: 'Contas a receber',
  icon: 'attach_money',
  path: '/accounts-receivable',
  exact: true,
  component: React.lazy(() =>
    import('app/views/finances/AccountsReceivablePage/AccountsReceivablePage')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
  allowedOnThemes: [Theme.CLAB],
}

export const financialDocumentsList = {
  name: 'Documentos Financeiros',
  icon: 'attach_money',
  path: '/financial-documents',
  exact: true,
  component: React.lazy(() =>
    import('app/views/finances/FinancialDocumentsList')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
  allowedOnThemes: [Theme.AMSM, Theme.CLAB, Theme.BIOACADEMIA],
}

export const calendar = {
  name: 'Calendário',
  icon: 'perm_contact_calendar',
  path: '/calendar',
  exact: true,
  component: React.lazy(() => import('app/views/calendar/Calendar')),
  roles: [
    USER_TYPE.ADMINISTRATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.FINANCIAL,
    USER_TYPE.BIOMETRY_MANAGER,
  ],
  capability: 'view_calendar_module',
  allowedOnThemes: [Theme.AMSM, Theme.CIC, Theme.AFA, Theme.EXOS, Theme.ACTION],
}

export const associationTitles = {
  name: 'Associados',
  icon: 'person',
  path: '/associados',
  exact: true,
  component: React.lazy(() =>
    import('app/views/association-title/AssociationTitleList')
  ),
  roles: [USER_TYPE.ADMINISTRATOR],
  allowedOnThemes: [Theme.AMSM],
}

export const notificationAnalytics = {
  name: 'Analytics de Notificações',
  icon: 'query_stats',
  path: '/notification-analytics',
  exact: true,
  component: React.lazy(() =>
    import('app/views/notifications/NotificationAnalytics')
  ),
  roles: [USER_TYPE.ADMINISTRATOR],
}

export const financialMovementsForecasting = {
  name: 'Previsão de movimentos financeiros',
  icon: 'attach_money',
  path: '/financial-movements-forecasting',
  exact: true,
  component: React.lazy(() =>
    import('app/views/finances/FinancialMovementsForecasting')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
}

export const financialDocumentsForecasting = {
  name: 'Previsão de documentos financeiros',
  icon: 'attach_money',
  path: '/financial-documents-forecasting',
  exact: true,
  component: React.lazy(() =>
    import('app/views/finances/FinancialDocumentsForecasting')
  ),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
}

export const paymentMethodsSync = {
  name: 'Sincronização de métodos de pagamento',
  icon: 'attach_money',
  path: '/payment-methods-sync',
  exact: true,
  component: React.lazy(() => import('app/views/finances/PaymentMethodsSync')),
  roles: [USER_TYPE.ADMINISTRATOR, USER_TYPE.FINANCIAL],
  capability: 'view_finances_module',
  allowedOnThemes: [Theme.AMSM, Theme.CLAB],
}

export const benefits = {
  name: 'Programa de benefícios',
  icon: 'loyalty',
  path: '/benefits',
  exact: true,
  allowedOnThemes: [Theme.AMSM],
  component: () => <ComingSoon image="/assets/images/benefits.svg" />,
  badge: {
    color: 'error',
    value: 'Em breve!',
  },
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const gym = {
  name: 'Academia',
  icon: 'sports_handball',
  path: '/gym',
  exact: true,
  component: React.lazy(() => import('app/views/gym/PersonalDashboard')),
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.TOTEM,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.BIOMETRY_MANAGER, // TODO: Filter based on access type instead of role
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
  capability: 'view_gym_module',
  allowedOnThemes: [
    Theme.HALL,
    Theme.AMSM,
    Theme.ACTION,
    Theme.GIBA,
    Theme.OFRAGOZO,
    Theme.IGORSANDI,
    Theme.GUILHERMELAZARETTI,
    Theme.LARISSACIOATO,
    Theme.DCFIT,
    Theme.EXOS,
  ],
}

export const gymTeacherDashboard = {
  name: 'Gestão da Academia',
  icon: 'groups',
  path: '/gym/manage',
  exact: true,
  component: React.lazy(() => import('app/views/gym/TeacherDashboard')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  capability: 'view_gym_staff_module',
  allowedOnThemes: [
    Theme.AMSM,
    Theme.ACTION,
    Theme.GIBA,
    Theme.OFRAGOZO,
    Theme.IGORSANDI,
    Theme.GUILHERMELAZARETTI,
    Theme.LARISSACIOATO,
    Theme.DCFIT,
    Theme.EXOS,
  ],
}

export const gymAccessDashboard = {
  name: 'Acessos à Academia',
  icon: 'sports_handball',
  path: '/gym-access',
  exact: true,
  component: React.lazy(() => import('app/views/gym/GymAccessDashboard')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  allowedOnThemes: [Theme.HALL, Theme.AMSM],
}

export const contracts = {
  name: 'Modelos de Contrato',
  icon: 'article',
  path: '/contracts',
  exact: true,
  component: React.lazy(() => import('app/views/contracts/ContractsDashboard')),
  roles: [USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  allowedOnThemes: [
    Theme.HALL,
    Theme.AMSM,
    Theme.ACTION,
    Theme.CLAB,
    Theme.EXOS,
    Theme.AFA,
  ],
}

export const contractsCrm = {
  name: 'CRM de Contratos',
  icon: 'article',
  path: '/contracts-crm',
  exact: true,
  component: React.lazy(() => import('app/views/contracts/ContractsCRM')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  capability: 'view_contracts_crm_module',
  allowedOnThemes: [
    Theme.HALL,
    Theme.AMSM,
    Theme.ACTION,
    Theme.EXOS,
    Theme.AFA,
  ],
}

export const campaigns = {
  name: 'Campanhas',
  icon: 'campaigns',
  path: '/campaigns',
  exact: true,
  component: React.lazy(() => import('app/views/campaigns/Campaigns')),
  roles: [USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  allowedOnThemes: [Theme.AMSM, Theme.ACTION, Theme.CIC],
}

export const findPhysicalSpaces = {
  name: 'Espaços Físicos',
  icon: 'deck',
  path: '/find-physical-spaces',
  exact: true,
  component: React.lazy(() =>
    import('app/views/physical-spaces/FindPhysicalSpaces')
  ),
  allowedOnThemes: [Theme.AMSM, Theme.CIC],
  // TODO: Keep minimum age condition for amsm
  // minimumAge: 18,
  userCondition: (userInfo, featureFlags) =>
    featureFlags?.ENABLE_SPACE_RESERVATION ||
    userInfo?.is_physical_space_reservation_staff ||
    [USER_TYPE.BIOMETRY_MANAGER, USER_TYPE.ADMINISTRATOR].includes(
      userInfo?.user_type
    ),
}

export const physicalSpaceDetails = {
  name: 'Espaços Físicos',
  icon: 'deck',
  path: '/physical-space/:id',
  exact: true,
  component: React.lazy(() =>
    import('app/views/physical-spaces/PhysicalSpaceDetails')
  ),
  roles: [
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.BIOMETRY_MANAGER,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const physicalEvaluationForm = {
  name: 'Academia',
  icon: 'sports_handball',
  path: '/physical-evaluation-form/:id?',
  exact: true,
  component: React.lazy(() => import('app/views/gym/PhysicalEvaluationForm')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
}

export const workouts = {
  name: 'Treinos',
  icon: 'sports_handball',
  path: '/workouts',
  exact: true,
  component: React.lazy(() => import('app/views/gym/Workouts')),
  roles: [
    USER_TYPE.TOTEM,
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}
export const findClasses = {
  name: 'Encontrar aulas',
  icon: 'search',
  path: '/find-classes',
  exact: true,
  component: React.lazy(() => import('app/views/gym/FindClasses')),
  roles: [
    USER_TYPE.TOTEM,
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}
export const activityClassesDetails = {
  name: 'Detalhes da aula',
  icon: 'school',
  path: '/activity-class-details/:id',
  exact: true,
  component: React.lazy(() => import('app/views/gym/ActivityClassDetails')),
  roles: [
    USER_TYPE.TOTEM,
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const workoutsDetails = {
  name: 'Treino',
  icon: 'sports_handball',
  path: '/workout/:id',
  exact: true,
  component: React.lazy(() => import('app/views/gym/WorkoutDetails')),
  roles: [
    USER_TYPE.TOTEM,
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],

  // bottomBar: {
  //   centerButton: {
  //     icon: <Icon>printer</Icon>,
  //     label: 'Imprimir',
  //   },
  // },
}

export const workoutPlayer = {
  name: 'Player de Treino',
  icon: 'play_arrow',
  path: '/workout-player/:id',
  exact: true,
  component: React.lazy(() => import('app/views/gym/WorkoutPlayer')),
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const workoutForm = {
  name: 'Formulário de Treino',
  icon: 'sports_handball',
  path: '/workout-form/:id?',
  exact: true,
  component: React.lazy(() => import('app/views/gym/WorkoutForm')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
}
export const activityclassform = {
  name: 'Formulário de Aula',
  icon: 'sports_handball',
  path: '/activity-class/:id?',
  exact: true,
  component: React.lazy(() => import('app/views/gym/ActivityClassForm')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
}

export const evaluations = {
  name: 'Avaliações',
  icon: 'sports_handball',
  path: '/evaluations',
  exact: true,
  component: React.lazy(() => import('app/views/gym/Evaluations')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
}

export const schools = {
  name: 'Modalidades',
  icon: 'sports_soccer',
  path: '/schools',
  exact: true,
  allowedOnThemes: [Theme.AMSM, Theme.AFA],
  component: () => <School />,
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
}

export const spaceRenting = {
  name: 'Aluguel de espaços',
  icon: 'house_siding',
  path: '/space-renting',
  exact: true,
  allowedOnThemes: [Theme.AMSM],
  component: React.lazy(() =>
    import('app/views/physical-space-renting/PhysicalSpaceRenting')
  ),
  userCondition: (userInfo, featureFlags) =>
    featureFlags?.ENABLE_SPACE_RESERVATION ||
    userInfo?.is_physical_space_reservation_staff ||
    [USER_TYPE.BIOMETRY_MANAGER, USER_TYPE.ADMINISTRATOR].includes(
      userInfo?.user_type
    ),
}

export const events = {
  name: 'Eventos',
  icon: 'celebration',
  path: '/events',
  exact: true,
  allowedOnThemes: [Theme.CIC],
  component: React.lazy(() => import('app/views/events/Events')),
}

export const tickets = {
  name: 'Cobranças',
  icon: 'local_atm',
  path: '/user-financial-documents',
  exact: true,
  allowedOnThemes: [Theme.AMSM],
  component: React.lazy(() =>
    import('./user-financial-documents/UserFinancialDocuments')
  ),
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const customReport = {
  name: 'Relatórios',
  icon: 'content_paste_search_icon',
  path: '/custom-report',
  exact: true,
  allowedOnThemes: [
    Theme.AMSM,
    Theme.ACTION,
    Theme.GIBA,
    Theme.OFRAGOZO,
    Theme.CIC,
    Theme.EXOS,
    Theme.AFA,
  ],
  component: React.lazy(() => import('./custom-report/CustomReport')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  capability: 'view_reports_module',
}

export const userPersonalData = {
  path: '/personal-data',
  exact: true,
  component: React.lazy(() => import('./users/UserPersonalData')),
  badge: {
    color: 'error',
    value: 'Em breve!',
  },
  bottomBar: {
    rightButton: {
      label: 'Editar',
      icon: <Icon>create</Icon>,
      onClick: () => {
        const state = Store.getState()
        Store.dispatch(
          modalFormOperations.showModalForm({
            app: 'users',
            model: 'user',
            id: state.auth?.userInfo?.id,
          })
        )
      },
    },
  },
}

export const physicalDeviceManagement = {
  name: 'Controle de acesso',
  icon: 'login',
  path: '/physical-device-management',
  exact: true,
  component: React.lazy(() =>
    import('./physical-device-management/PhysicalDeviceManagement')
  ),
  allowedOnThemes: [Theme.AMSM],
  roles: [USER_TYPE.ADMINISTRATOR],
}

export const survey = {
  name: 'Questionário',
  icon: 'file',
  path: '/survey',
  exact: true,
  component: React.lazy(() => import('./survey/SurveyForm')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
}

// Survey builder - gated by the `survey_management` feature flag (see
// docs/FEATURE_FLAGS.md; seed migration: amsm-backend
// hall/feature_flags/migrations/0013_seed_survey_management_flag.py).
// Like `internalCampaigns`, flags are re-fetched on every page load, so the
// route stays reachable while they're pending and the pages redirect to 404
// themselves once the flag resolves to off.
const isSurveyManagementAllowed = () => {
  const state = Store.getState()
  return (
    !featureFlagsTimestamp(state) ||
    getFeatureFlag(state, SURVEY_MANAGEMENT_FLAG)
  )
}

export const surveyBuilder = {
  name: 'Questionários',
  icon: 'assignment',
  path: SURVEY_BUILDER_PATH,
  exact: true,
  component: React.lazy(() => import('./survey/builder/SurveyBuilderList')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  userCondition: isSurveyManagementAllowed,
}

export const surveyBuilderEditor = {
  name: 'Questionário',
  icon: 'assignment',
  path: `${SURVEY_BUILDER_PATH}/:id`,
  exact: true,
  component: React.lazy(() => import('./survey/builder/SurveyBuilderEditor')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  userCondition: isSurveyManagementAllowed,
}

export const totem = {
  name: 'TotemDisplay',
  path: '/totem-display',
  exact: true,
  component: React.lazy(() => import('./totem/TotemDisplay')),
  roles: [USER_TYPE.TOTEM, USER_TYPE.ADMINISTRATOR],
  hideLayout: true,
}

export const healthInsuranceComparisonReport = {
  name: 'Planos de Saúde',
  icon: 'health_and_safety',
  path: '/health-insurance-comparison-report',
  exact: true,
  component: React.lazy(() =>
    import('app/views/health-insurance/HealthInsuranceComparisonReport')
  ),
  roles: [USER_TYPE.ADMINISTRATOR],
  allowedOnThemes: [Theme.AMSM],
}

export const lifeInsuranceComparisonReport = {
  name: 'Seguros de Vida',
  icon: 'health_and_safety',
  path: '/life-insurance-comparison-report',
  exact: true,
  component: React.lazy(() =>
    import('app/views/life-insurance/LifeInsuranceComparisonReport')
  ),
  roles: [USER_TYPE.ADMINISTRATOR],
  allowedOnThemes: [Theme.AMSM],
}

export const physicalEvaluationHistory = {
  name: 'Planos de Saúde',
  icon: 'health_and_safety',
  path: '/gym/physical-evaluation-history',
  exact: true,
  component: React.lazy(() =>
    import('app/views/gym/PhysicalEvaluationHistory')
  ),
  isPublic: false,
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

export const healthSignalAnalysis = {
  name: 'Análise de Sinais Vitais',
  icon: 'monitor_heart',
  path: '/health-signals/analysis',
  exact: true,
  component: React.lazy(() => import('app/views/health/HealthSignalAnalysis')),
  isPublic: false,
  roles: [
    USER_TYPE.MEMBER,
    USER_TYPE.RELATIVE,
    USER_TYPE.RESPONSIBLE,
    USER_TYPE.ASSOCIATE,
    USER_TYPE.DIRECTOR,
    USER_TYPE.COLLABORATOR,
    USER_TYPE.MANAGER,
    USER_TYPE.ADMINISTRATOR,
  ],
}

// export const customDashboard = {
//   path: '/custom-dashboard/:slug',
//   exact: true,
//   component: React.lazy(() => import('app/views/CustomDashboard')),
//   isPublic: false,
// }

export const analyticsDashboard = {
  name: 'Analytics',
  icon: 'insights',
  path: '/analytics',
  exact: true,
  component: React.lazy(() => import('app/views/analytics/AnalyticsDashboard')),
  roles: [USER_TYPE.ADMINISTRATOR],
}

export const aiUsageDashboard = {
  name: 'Uso de IA',
  icon: 'smart_toy',
  path: '/ai-usage',
  exact: true,
  component: React.lazy(() => import('app/views/ai-usage/AIUsageDashboard')),
  roles: [USER_TYPE.ADMINISTRATOR],
}

// "Conectar assistente de IA" (MCP) — the setup page and the OAuth consent page.
// Neither uses `userCondition` for the `mcp_server_access` flag: flags load
// asynchronously, so a hard reload would bounce to /session/404 before they
// arrive. The backend enforces the flag instead (404 on every
// /backend/mcp/api/* endpoint when off) and both pages render that as "not
// available"; the menu entries pointing here check the flag themselves.
export const mcpSetup = {
  name: 'Conectar assistente de IA',
  icon: 'smart_toy',
  path: MCP_SETUP_PATH,
  exact: true,
  component: React.lazy(() => import('app/views/mcp/McpSetup')),
  roles: MCP_SETUP_ROLES,
}

// Public on purpose: the AI assistant sends users here straight from outside the app.
// The page itself sends anyone logged out to the login and brings them back
// (app/reusable/postLoginRedirect.js).
export const mcpAuthorize = {
  path: MCP_AUTHORIZE_PATH,
  exact: true,
  component: React.lazy(() => import('app/views/mcp/McpAuthorize')),
  isPublic: true,
  hideLayout: true,
}

export const tasksBoard = {
  name: 'Quadro de Tarefas',
  icon: 'assignment',
  path: '/tasks-board',
  exact: true,
  component: React.lazy(() => import('app/views/management/TasksBoard')),
  roles: [USER_TYPE.COLLABORATOR, USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR],
  capability: 'view_tasks_board_module',
}

// Nutrition module - phase 0/1 (MVP logging). Gated by the `nutrition_module`
// feature flag (see docs/FEATURE_FLAGS.md; seed migration:
// amsm-backend hall/feature_flags/migrations/0004_seed_nutrition_module_flag.py) -
// off for every tenant until turned on, starting with AMSM as the pilot.
// `userCondition` is checked both for route access (`AppRoute` in App.jsx)
// and nav visibility (`shouldRenderNavigation`, used by both the legacy
// `navigations.js` sidenav and the new `sections.js` section rail), so
// gating here covers every nav surface with one flag check.
const NUTRITION_ROLES = [
  USER_TYPE.MEMBER,
  USER_TYPE.RELATIVE,
  USER_TYPE.RESPONSIBLE,
  USER_TYPE.ASSOCIATE,
  USER_TYPE.DIRECTOR,
  USER_TYPE.COLLABORATOR,
  USER_TYPE.MANAGER,
  USER_TYPE.ADMINISTRATOR,
]

const isNutritionModuleEnabled = () =>
  getFeatureFlag(Store.getState(), 'nutrition_module')

export const nutritionDiary = {
  name: 'Nutrição',
  icon: 'restaurant',
  path: '/nutrition/diary',
  exact: true,
  component: React.lazy(() => import('app/views/nutrition/NutritionDiary')),
  roles: NUTRITION_ROLES,
  userCondition: isNutritionModuleEnabled,
}

export const nutritionProfileForm = {
  name: 'Perfil Nutricional',
  icon: 'restaurant',
  path: '/nutrition/profile/:id?',
  exact: true,
  component: React.lazy(() =>
    import('app/views/nutrition/NutritionProfileForm')
  ),
  roles: NUTRITION_ROLES,
  userCondition: isNutritionModuleEnabled,
}

// Reached from the top-bar "Novidades" button (InternalCampaignsButton), not
// from the section/side navigation.
export const internalCampaigns = {
  name: 'Novidades',
  icon: 'auto_awesome',
  path: INTERNAL_CAMPAIGNS_PATH,
  exact: true,
  component: React.lazy(() =>
    import('app/views/internalCampaigns/InternalCampaigns')
  ),
  roles: INTERNAL_CAMPAIGN_ROLES,
  // Feature flags are cleared on every page load and re-fetched right
  // after, so a reload on /novidades would otherwise 404 before they
  // arrive. While they're pending the page renders its own loading state
  // and redirects to 404 itself once they resolve to off.
  userCondition: () => {
    const state = Store.getState()
    return (
      !featureFlagsTimestamp(state) ||
      getFeatureFlag(state, INTERNAL_CAMPAIGNS_FLAG)
    )
  },
}
